import * as THREE from "three";
import debug from "debug";

const log = debug("app:explodedview");

class ExplodedView {
  private experience: any;
  private scene: THREE.Scene;
  private eventHub: any;
  private originalPositions: Map<THREE.Object3D, number> = new Map();
  private lastSliderValue: number = 1;
  private debounceTimer: any = null;

  constructor(experience: any) {
    this.experience = experience;
    this.scene = experience.scene;
    this.eventHub = experience.eventHub;

    log("Initialized ExplodedView");
    this.init();
  }

  init() {
    this.eventHub.on("explodedViewChange", (value: number) => {
      this.lastSliderValue = value;
      this.applyExplodedView(value);
    });

    this.eventHub.on("explosionStop", () => {
      if (this.lastSliderValue >= 0.99) {
        log("explosionStop skipped - slider is at originalY");
        return;
      }

      this.debounce(() => {
        log("Reapplying exploded view from explosionStop event");
        this.applyExplodedView(this.lastSliderValue);
      }, 200); // debounce delay in ms
    });
  }

  cleanup() {
    this.eventHub.off("explodedViewChange", this.applyExplodedView);
    this.eventHub.off("explosionStop", this.applyExplodedView);
    log("Cleaned up ExplodedView listeners");
  }

  debounce(func: Function, delay: number) {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => func(), delay);
  }

  applyExplodedView(sliderValue: number) {
    const BASE_LAYER = 20;
    const EXPLOSION_INTENSITY = 7;
    const boostMultiplier = 5;
    const boostedLayers = new Set([70, 50, 40, 35, 30, 10]);
    const excludedLayers = new Set([20, 37, 90]);

    this.scene.traverse((object) => {
      if (!("layer" in object.userData)) return;

      const layer = object.userData.layer;

      if (!this.originalPositions.has(object)) {
        this.originalPositions.set(object, object.position.y);
        log(`Stored originalY for layer ${layer}:`, object.position.y);
      }

      const originalY = this.originalPositions.get(object)!;

      if (excludedLayers.has(layer)) {
        object.position.y = originalY;
        return;
      }

      const offsetMultiplier = (layer - BASE_LAYER) / 10;
      const clampedValue = sliderValue >= 0.99 ? 1 : sliderValue;
      let offset = offsetMultiplier * (1 - clampedValue) * EXPLOSION_INTENSITY;

      if (boostedLayers.has(layer)) {
        offset *= boostMultiplier;
      }

      object.position.y = originalY + offset;

      log(
        `Layer ${layer} ${boostedLayers.has(layer) ? "[boosted]" : ""} | ` +
          `originalY: ${originalY.toFixed(3)} | multiplier: ${offsetMultiplier} | ` +
          `slider: ${sliderValue.toFixed(2)} | offset: ${offset.toFixed(3)} | finalY: ${object.position.y.toFixed(3)}`
      );
    });
  }
}

export default ExplodedView;
